'use client';

import { useState } from 'react';
import Image from 'next/image';
import { PhotoIcon } from '@heroicons/react/20/solid';

import { CircularLoader } from '../../loaders';

interface IPreviewImageThumbnailProps {
  /**
   * The base url of the image hosting service.
   */
  basePath: string;
  /**
   * The url segment that resolved the image file.
   */
  filePath: string;
  /**
   * Callback to trigger a model that previews the file/image.
   */
  onPreview: (filePath: string) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This shows a thumbnail preview of an image loaded from the path provided.
 */
export function PreviewImageThumbnail({
  basePath,
  filePath,
  onPreview,
}: IPreviewImageThumbnailProps) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  if (!basePath) return null;

  const imageSrc = `${basePath}${filePath}`;

  const handleOnLoadImage = () => {
    setIsImageLoaded(true);
  };

  const handleOnClickPreview = () => {
    onPreview?.(imageSrc);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className="
        outline-primary-400 hover:bg-primary-200 focus:bg-primary-50 active:bg-primary-200 group flex h-full w-full
        flex-1 flex-col items-center justify-center overflow-hidden rounded-xl
        p-1 outline-1 ring-0
      "
      onClick={handleOnClickPreview}
      role="button"
      tabIndex={0}
    >
      {!isImageLoaded ? (
        <>
          <PhotoIcon className="absolute h-16 w-16 text-gray-200" />
          <CircularLoader className="absolute h-8 w-8 text-gray-200 opacity-20" />
        </>
      ) : null}

      <Image
        alt=""
        className="z-[1] h-full w-full rounded-lg object-cover"
        height={200}
        onLoad={handleOnLoadImage}
        src={imageSrc}
        width={200}
      />
    </div>
  );
}
